import type { CreateDocumentFromTemplatePlaceholder } from '../../../../models/Document';
import type { DocumentTemplatePlaceholder } from '../../../../models/DocumentTemplate';

export type PlaceholderEntityType =
  | 'internalOffice'
  | 'company'
  | 'office'
  | 'contact'
  | 'projectMember'
  | 'projectPhase'
  | 'freetext';

export const ROOT_ENTITY_TO_TYPE: Record<string, PlaceholderEntityType> = {
  internaloffice: 'internalOffice',
  company: 'company',
  office: 'office',
  contact: 'contact',
  projectmember: 'projectMember',
  projectphase: 'projectPhase',
  businessunit: 'office',
};

export const AUTO_RESOLVED_ENTITIES = new Set(['project', 'creator']);

export const AUTO_RESOLVED_STATIC_PLACEHOLDERS = new Set(['today']);

export const freetextFieldName = (placeholderName: string) =>
  `freetext__${placeholderName}`;

interface BuildPlaceholdersContext {
  projectId: string;
  creatorId: string;
  projectMemberIdMap: Record<string, string>;
}

export const buildCreateDocumentPlaceholders = (
  templatePlaceholders: DocumentTemplatePlaceholder[] | undefined,
  formValues: Record<string, string>,
  context: BuildPlaceholdersContext
): CreateDocumentFromTemplatePlaceholder[] => {
  const seenGroups = new Set<string>();
  const placeholders: CreateDocumentFromTemplatePlaceholder[] = [];
  const trailing: CreateDocumentFromTemplatePlaceholder[] = [];

  for (const p of templatePlaceholders || []) {
    const path = p.path || '';
    const rootEntity = path.split('.')[0]?.toLowerCase();
    const group = p.group;
    const hasGroup = !!group;

    let entityId = '';
    let isFreetext = false;
    let ignoresEntityId = false;

    const entityType = ROOT_ENTITY_TO_TYPE[rootEntity];
    if (entityType) {
      const formFieldName = hasGroup ? `${entityType}__${group}` : entityType;
      entityId = formValues[formFieldName] || '';

      if (entityType === 'projectMember' && entityId) {
        if (context.projectMemberIdMap[entityId]) {
          entityId = context.projectMemberIdMap[entityId];
        } else {
          console.warn(
            `Missing projectMemberId mapping for contactId: ${entityId}`
          );
        }
      }
    } else if (rootEntity === 'project') {
      entityId = context.projectId || '';
    } else if (rootEntity === 'creator') {
      entityId = context.creatorId || '';
    } else if (rootEntity === 'staticplaceholder') {
      const subType = path.split('.')[1]?.toLowerCase();
      if (AUTO_RESOLVED_STATIC_PLACEHOLDERS.has(subType)) {
        ignoresEntityId = true;
      } else {
        isFreetext = true;
        entityId = formValues[freetextFieldName(p.name)] || '';
      }
    } else {
      isFreetext = true;
      entityId = formValues[freetextFieldName(p.name)] || '';
    }

    if (hasGroup && !isFreetext) {
      if (seenGroups.has(group)) continue;
      seenGroups.add(group);
      placeholders.push({ entityId, group });
    } else if (!hasGroup && ignoresEntityId) {
      trailing.push({ entityId, name: p.name });
    } else {
      placeholders.push({ entityId, name: p.name });
    }
  }

  return [...placeholders, ...trailing];
};
